import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import { getOrganization, getOrgProducts, getOrgCategories } from "../../utility";
import { addItemToCart } from "../../redux/cart/reducer";
import { AllIcon, BasketIcon, CardIcon } from "../../components/Icon";
import CashlessModal from "./CashlessModal";
import LoadingSpinner from "../../components/spinners/DrinkSpinner";
import { CategoriesArray } from "../../utility/data";
import { Icons } from "../../images/icons";
import CareModal from "./CareModal";
import { useTranslation } from "react-i18next";
import getIcon from "../../utility/getIcon";
import OptionsModal from "../../components/optionsModal/OptionsModal";
import getOrgOptionsCategories from "../../utility/getOrgOptionsCategories";
import getOrgOptionsProducts from "../../utility/getOrgOptionsProducts";
import { getStockItems } from "../../utility/getStock";
import {
  canAddStockManagedProduct,
  filterSellableProducts,
  hydrateProductsWithStock,
} from "../../utility/stockManagement";

const getDefinedCategories = (availableProducts, hardcodedCategories) => {
  const productsCategories = new Set(availableProducts.map((product) => product.category));
  return hardcodedCategories.filter((category) => productsCategories.has(category.value));
};

const mapFetchedCategories = (fetchedCategories) => {
  return fetchedCategories?.map((category) => ({
    label: category.title,
    value: category.title,
    icons: CategoriesArray.find((cat) => cat.name === category.customIcon)?.icon,
    hideFromFastpass: category.hideFromFastpass,
  }));
};

const filterCategories = (allCategories, availableProducts) => {
  const productsCategories = new Set(availableProducts.map((product) => product.category));

  return allCategories.filter(
    (category) =>
      (productsCategories.has(category.value) || productsCategories.has(category.label)) &&
      !category.hideFromFastpass,
  );
};
const Cashless = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation("fastpass");
  const [modalOpen, setModalOpen] = useState(false);
  const [careModalOpen, setCareModalOpen] = React.useState(false);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState(t("loading"));
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [bailProducts, setBailProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentOptionsCategory, setCurrentOptionsCategory] = useState([]);
  const [total, setTotal] = useState(0);
  const optionsCategories = useRef([]);
  const optionParentId = useRef(null);

  const navigate = useNavigate();
  const { orgId } = useParams();
  const cartStore = useSelector((state) => state.cartReducer.cartByOrg[orgId] || []);
  const alert = useAlert();

  const getHardcodedCategories = () => [
    { label: t("beers"), value: "Beers", icons: Icons.beer },
    { label: t("food"), value: "Food", icons: Icons.food },
    { label: t("softs"), value: "Softs", icons: Icons.soft },
    { label: t("cocktails"), value: "Cocktails", icons: Icons.cocktail },
    { label: t("wine"), value: "Wine", icons: Icons.wine },
    { label: t("hot_drinks"), value: "Hot Drinks", icons: Icons.hotDrink },
    { label: t("others"), value: "Other", icons: Icons.others },
  ];

  const fetchCategories = async (orgId) => {
    const fetchedCategories = await getOrgCategories(orgId);
    if (!fetchedCategories) {
      alert.show(t("error_fetch_categories"), {
        type: "error",
      });
      throw new Error("Failed to fetch categories");
    }
    return fetchedCategories.categories;
  };

  const getOrganizationHandler = async () => {
    setLoading(true);
    try {
      const orgData = await getOrganization(orgId);
      const loadedProducts = await getOrgProducts(orgId);
      const orgOptionsCategories = await getOrgOptionsCategories(orgId);
      const orgOptionsProducts = await getOrgOptionsProducts(orgId);
      const stockItems = orgData.org?.uid ? await getStockItems(orgData.org.uid) : [];
      const hydratedOptionProducts = hydrateProductsWithStock(orgOptionsProducts, stockItems);

      const optionsCategoriesFormatted = orgOptionsCategories
        ?.map((option) => {
          const associatedProducts = hydratedOptionProducts?.filter((optionProduct) => {
            return (
              optionProduct.category === option.title &&
              filterSellableProducts([optionProduct]).length > 0 &&
              (optionProduct.availableQuantity === false || optionProduct.availableQuantity > 0)
            );
          });

          return { ...option, products: associatedProducts };
        })
        .filter((option) => option.products && option.products.length > 0);

      optionsCategories.current = optionsCategoriesFormatted;

      if (loadedProducts?.products) {
        const availableProducts = filterSellableProducts(
          hydrateProductsWithStock(loadedProducts.products, stockItems),
        );
        setProducts(availableProducts);
        setFilteredProducts(availableProducts.sort((a, b) => Number(a.price) - Number(b.price)));

        const findBails = availableProducts.filter(
          (product) =>
            product.title.includes("caution") ||
            product.title.includes("bail") ||
            product.title.includes("Caution") ||
            product.title.includes("Bail") ||
            product.title.includes("Kaution") ||
            product.title.includes("Borg") ||
            product.title.includes("menu"),
        );
        setBailProducts(findBails);

        const hardcodedCategories = getHardcodedCategories();
        const definedCategories = getDefinedCategories(availableProducts, hardcodedCategories);

        const fetchedCategories = await fetchCategories(orgId);
        const mappedFetchedCategories = mapFetchedCategories(fetchedCategories);

        const allCategories = [...definedCategories, ...(mappedFetchedCategories || [])];
        const filteredCategories = filterCategories(allCategories, availableProducts);
        setCategories(filteredCategories);
      } else {
        alert.show(t("errorNoProducts"), { type: "error" });
      }
      const total =
        cartStore?.reduce((total, item) => total + parseFloat(item?.totalDue || 0), 0) || 0;

      setOrgName(orgData.org.organization);
      setTotal(total);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const addToCart = (item) => {
    if (!canAddStockManagedProduct(item, cartStore)) {
      alert.show(t("maxQuantityReached"), { type: "error" });
      return;
    }

    if (item.availableQuantity !== false) {
      const index = cartStore.findIndex((product) => product.id === item.id);
      if (index !== -1 && item.availableQuantity - cartStore[index].quantity <= 0) {
        alert.show(t("maxQuantityReached"), { type: "error" });
        return;
      }
    }

    if (!!item.optionCategories?.length) {
      const currentDate = Date.now();
      optionParentId.current = `${item.id}:${currentDate}`;
      dispatch(
        addItemToCart({
          orgId,
          item: {
            ...item,
            id: `${item.id}:${currentDate}`,
          },
        }),
      );
    } else {
      dispatch(addItemToCart({ orgId, item: item }));
    }

    setTotal((prev) => prev + parseFloat(item.price));
  };

  const addOptionsToCart = (options) => {
    let totalPrice = 0;
    const pendingCart = [...cartStore];

    options.forEach((option) => {
      if (!canAddStockManagedProduct(option, pendingCart)) {
        alert.show(t("maxQuantityReached"), { type: "error" });
        return;
      }

      const optionFormatted = {
        ...option,
        parentId: optionParentId.current,
        id: `${option.id}:${Date.now()}`,
      };

      totalPrice += parseFloat(option.price);
      pendingCart.push({ ...optionFormatted, quantity: 1, totalDue: parseFloat(option.price) });
      dispatch(addItemToCart({ orgId, item: optionFormatted }));
    });

    setTotal((prev) => prev + parseFloat(totalPrice));
  };

  useEffect(() => {
    getOrganizationHandler();
  }, [orgId]);

  useEffect(() => {
    let filtered;
    if (active === "all") {
      filtered = products.filter((product) =>
        categories.some((cat) => cat.value === product.category),
      );
    } else {
      filtered = products.filter((product) => product.category === active);
    }

    setFilteredProducts(filtered.sort((a, b) => a.price - b.price));
  }, [active, products, categories]);

  return (
    <div className="d-flex flex-column" style={{ minHeight: "100dvh" }}>
      <div className="page-header pb-3 position-sticky top-0 zi-50">
        <div className="container">
          <h2 className="title text-center mb-3 text-30">{orgName}</h2>
        </div>
        <div className="filter-menu-container d-flex">
          <ul className="filter-menu px-4 mx-auto my-0">
            <li
              key={0}
              className={active === "all" ? "active" : ""}
              onClick={() => setActive("all")}
            >
              <AllIcon />
            </li>

            {categories.map((item, index) => (
              <li
                key={index}
                className={active === item.value ? "active" : ""}
                onClick={() => setActive(item.value)}
              >
                <img src={item.icons} color="white" height="50%" width="50%" alt="" />
                {item.icon}
                {item.label}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {orgName === t("loading") ? (
        <div className="d-flex align-items-center justify-content-center flex-grow-1">
          <LoadingSpinner height="100%" width="100%" />
        </div>
      ) : (
        <div className="d-flex flex-column flex-grow-1">
          <div className="container py-3 px-4 flex-grow-1">
            <div className="row g-3">
              {filteredProducts.map((item, i) => (
                <div className="col-6 col-sm-4 col-md-3" key={i}>
                  <CaiseMenuBox
                    itemData={item}
                    cart={cartStore}
                    addToCart={(item) => addToCart(item)}
                    optionsCategories={optionsCategories.current}
                    setCurrentOptionsCategory={setCurrentOptionsCategory}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="container position-sticky bottom-0 pb-2 gap-2 d-flex zi-50">
            <button
              type="button"
              className="btn-1 w-0 flex-grow"
              onClick={() =>
                navigate(`/cashless/${orgId}/cart`, {
                  state: { bailProducts },
                })
              }
            >
              <BasketIcon />
              {t("cartTotal", { total: total.toFixed(2) })}
            </button>
            <button
              type="button"
              className="btn-2 w-0 flex-grow"
              onClick={() => {
                if (bailProducts.length > 0) {
                  setModalOpen(true);
                } else {
                  navigate(`/cashless/${orgId}/payment`);
                }
              }}
            >
              <CardIcon />
              {t("payment")}
            </button>
          </div>
        </div>
      )}

      <CashlessModal
        open={modalOpen}
        setOpen={setModalOpen}
        total={total}
        bails={bailProducts}
        setCareModalOpen={setCareModalOpen}
      />
      <OptionsModal
        visible={currentOptionsCategory?.length > 0}
        categories={currentOptionsCategory}
        onSelectOptions={addOptionsToCart}
        onClose={() => setCurrentOptionsCategory([])}
        orgId={orgId}
      />
      <CareModal open={careModalOpen} setOpen={setCareModalOpen} />
    </div>
  );
};

const CaiseMenuBox = ({
  itemData,
  cart,
  addToCart,
  optionsCategories,
  setCurrentOptionsCategory,
}) => {
  const cartItems = cart?.filter(
    (item) => item.id === itemData?.id || item.id.split(":")[0] === itemData?.id,
  );
  const totalQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const handleAddCart = (itemData) => {
    if (itemData?.optionCategories?.length > 0) {
      const optionsCategoriesFiltered = optionsCategories.filter((option) =>
        itemData.optionCategories.includes(option.id),
      );
      setCurrentOptionsCategory(optionsCategoriesFiltered, itemData.id);
    }
    addToCart(itemData);
  };

  return (
    <div className="caise-menu-box">
      <div className="click" onClick={() => handleAddCart(itemData)} />
      {!!totalQuantity && <div className="close-2">x{totalQuantity}</div>}
      <div className="productImg">
        <img src={getIcon(itemData?.category, itemData?.customIcon)} alt={itemData.title} />
      </div>

      <h6 className="name">{itemData?.title}</h6>
      <h6 className="price">{itemData?.price}€</h6>
    </div>
  );
};

export default Cashless;
